import React from 'react'
import { Box, Button, Chip } from '@mui/joy'
import CallEndIcon from '@mui/icons-material/CallEnd'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import PersonIcon from '@mui/icons-material/Person'
import HeadsetIcon from '@mui/icons-material/Headset'
import HeadsetOffIcon from '@mui/icons-material/HeadsetOff'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { CallStatus } from '@numeo/types'

interface AICallControlsProps {
    onStopCall: () => void
    onTakeOver: () => void
    takingOver: boolean
    // Listen feature props
    onToggleListen: () => void
    isListening: boolean
    callStatus: CallStatus
    takenOver?: boolean
    endingCall: boolean
}

export const AICallControls: React.FC<AICallControlsProps> = ({
    onStopCall,
    onTakeOver,
    takingOver,
    endingCall,
    onToggleListen,
    isListening,
    callStatus,
    takenOver = false,
}) => {
    const canTakeOver = !endingCall && !takingOver && !takenOver // NEW: Also check initialization

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* AI Status Indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <SmartToyIcon color="primary" />
                <Chip color={callStatus === CallStatus.CONNECTED ? 'success' : 'primary'} variant="soft" size="sm">
                    AI Assistant{' '}
                    {{
                        [CallStatus.INITIATING]: 'Initializing...',
                        [CallStatus.CONNECTED]: 'Active',
                        [CallStatus.BUSY]: 'Busy',
                        [CallStatus.ENDED]: 'Ended',
                        [CallStatus.ERROR]: 'Error',
                    }[callStatus] || ''}
                </Chip>

                {/* Listening indicator */}
                {isListening && (
                    <Chip
                        color="primary"
                        variant="soft"
                        size="sm"
                        startDecorator={
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: 'primary.500',
                                }}
                            />
                        }
                    >
                        Listening
                    </Chip>
                )}
            </Box>

            {/* Control Buttons */}
            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                {/* Listen Button - Only show for active AI calls that haven't been taken over */}
                {!takenOver && (
                    <Button
                        variant="soft"
                        color={isListening ? 'warning' : 'neutral'}
                        onClick={onToggleListen}
                        startDecorator={isListening ? <HeadsetOffIcon /> : <HeadsetIcon />}
                        fullWidth
                        sx={{
                            cursor: 'pointer',
                            opacity: 1,
                        }}
                    >
                        {isListening ? 'Stop Listening' : 'Listen to Call'}{' '}
                    </Button>
                )}

                {/* Take Over Button */}
                {!takenOver ? (
                    <Button
                        variant="soft"
                        color="primary"
                        disabled={!canTakeOver}
                        loading={takingOver}
                        onClick={onTakeOver}
                        startDecorator={<PersonIcon />}
                        fullWidth
                        sx={{
                            opacity: canTakeOver ? 1 : 0.6,
                            cursor: canTakeOver ? 'pointer' : 'not-allowed',
                        }}
                    >
                        {takingOver ? 'Taking Over...' : 'Take Over'}
                    </Button>
                ) : (
                    <Button
                        variant="soft"
                        color="success"
                        disabled={true}
                        startDecorator={<CheckCircleIcon />}
                        fullWidth
                        sx={{
                            cursor: 'default',
                            '&.Mui-disabled': {
                                opacity: 1,
                            },
                        }}
                    >
                        Call Taken Over
                    </Button>
                )}

                {/* End Call Button */}
                <Button variant="soft" color="danger" loading={endingCall} onClick={onStopCall} startDecorator={<CallEndIcon />} fullWidth>
                    End AI Call
                </Button>
            </Box>
        </Box>
    )
}
